import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, Dimensions, ScrollView, Platform, FlatList } from 'react-native'
import { Button, Text, Spinner, Layout, RangeDatepicker, Card} from '@ui-kitten/components'
import { getSalesOnDateRange, saveNewMarketingList } from '../../global/Firebase/firebase'
import { UserContext } from '../../global/UserProvider'
import Divider from '../../components/Divider'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { dateRangeWithTimezoneAndAutoclose } from '../../utils/timezoneFunctions'
import { useAtomValue } from 'jotai'
import { localeDateServiceAtom, productCategoriesListAtom, storesDictAtom } from '../../global/AtomsProvider'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'

export default function CreateMarketingListScreen( { navigation, renderItem } ) {
  const { t } = useTranslation()
  const startRange = () => {
    var today = new Date()
    var yesterday = new Date()
    var ago = new Date()
    yesterday.setDate(today.getDate() - 1)
    ago.setDate(today.getDate() - 3)
    return {
      startDate: ago, 
      endDate: yesterday
    }
  }
  const [context, setContext] = useContext(UserContext)
  const productCategoriesList = useAtomValue(productCategoriesListAtom)
  const db = context.db
  const [data, setData] = useState(null)
  const [store, setStore] = useState(null)
  const [productCategory, setProductCategory] = useState(null)
  const [product, setProduct] = useState(null)
  const [range, setRange] = useState(startRange)
  const [isLoading, setIsLoading] = useState(false)
  const _rangePicker = React.createRef()
  const localeDateService = useAtomValue(localeDateServiceAtom)

  const getClientsByProductCategory = async (categoryID) => {
    if (range.startDate != null && range.endDate != null) {
      setIsLoading(true)
      _rangePicker.current.blur()
      setData(null)
      let localRange = dateRangeWithTimezoneAndAutoclose(range)
      const sales = await getSalesOnDateRange(db, localRange, true, store, null, true)
      
      // [{...clientInfo, products: [{name, id, checks, quantity}]}]
      // add client only once
      var clients = []
      var clientsObject = {}
      for (const [index, value] of sales.entries()) {
        for (const [cartItemIndex, cartItem] of value.cartItems.entries()) {
          if (cartItem.categoryID === categoryID) {
            var clientInfo = {...clientsObject[value.clientId], ...value.client, totalQuantity: (clientsObject[value.clientId]?.totalQuantity || 0) + cartItem.quantity}
            var newProductsObject = {...clientInfo.products}
            newProductsObject[cartItem.id] = {
              ...newProductsObject[cartItem.id], 
              name: cartItem.name,
              id: cartItem.id,
              totalChecks: (newProductsObject[cartItem.id]?.totalChecks || 0) + 1,
              totalQuantity: (newProductsObject[cartItem.id]?.totalQuantity || 0) + cartItem.quantity,
            }
            clientInfo.products = newProductsObject
            clientInfo.querySource = 'sales'
            clientsObject[value.clientId] = clientInfo
          }
        }
      }
      var clients = []
      for (var key in clientsObject) {
        clients.push(clientsObject[key])
      }
      clients.sort((a, b) => b.totalQuantity - a.totalQuantity)
      setData(clients)
      setIsLoading(false)
    }
  }

  const getClientsByProduct = async (product) => {
    if (range.startDate != null && range.endDate != null) {
      setIsLoading(true)
      _rangePicker.current.blur()
      setData(null)
      setProduct(product)
      let localRange = dateRangeWithTimezoneAndAutoclose(range)
      const sales = await getSalesOnDateRange(db, localRange, true, store, null, true)
      
      // [{...clientInfo, products: [{name, id, checks, quantity}]}]
      // add client only once
      var clients = []
      var clientsObject = {}
      for (const [index, value] of sales.entries()) {
        for (const [cartItemIndex, cartItem] of value.cartItems.entries()) {
          if (cartItem.id === product.id) {
            var clientInfo = {...clientsObject[value.clientId], ...value.client, totalQuantity: (clientsObject[value.clientId]?.totalQuantity || 0) + cartItem.quantity}
            var newProductsObject = {...clientInfo.products}
            newProductsObject[cartItem.id] = {
              ...newProductsObject[cartItem.id], 
              name: cartItem.name,
              id: cartItem.id,
              totalChecks: (newProductsObject[cartItem.id]?.totalChecks || 0) + 1,
              totalQuantity: (newProductsObject[cartItem.id]?.totalQuantity || 0) + cartItem.quantity,
            }
            clientInfo.products = newProductsObject
            clientInfo.querySource = 'sales'
            clientsObject[value.clientId] = clientInfo
          }
        }
      }
      var clients = []
      for (var key in clientsObject) {
        clients.push(clientsObject[key])
      }
      clients.sort((a, b) => b.totalQuantity - a.totalQuantity)
      setData(clients)
      setIsLoading(false)
    }
  }
  
  return (    
    <ScrollView>
      <View style={styles.dateFilter}>
        <StoreChooserHorizontal store={store} onChange={(newStore) => {
          setStore(newStore)
        }} />
        <Text category='label' style={styles.label}>
          {t('filterByDate')}
        </Text>
        <RangeDatepicker
          placement='top'
          dateService={localeDateService}
          range={range}
          min={new Date('2020-12-01')}
          ref={_rangePicker}
          onSelect={(newRange) => {
            if (newRange.endDate) {
              setRange(newRange)
            } else if (newRange.startDate && range.startDate === newRange.startDate) {
              let endDate = new Date(newRange.startDate)
              endDate.setHours('23', '59', '59', '99')
              setRange({startDate: newRange.startDate, endDate})
            } else {
              setRange(newRange)
            }
          }}
        />
        <Text category='label' style={styles.label}>
          {t('marketingLists.byProductCategory')}
        </Text>
        <PlatformDependantSelect
          data={productCategoriesList}
          selectedOption={productCategory}
          onSelect={setProductCategory}
        />
        <Button style={{marginVertical: 10}} onPress={() => {
          if (productCategory != null) {
            getClientsByProductCategory(productCategory.id)
          }
        }}>
          {t('find')}
        </Button>

        <Text category='label' style={styles.label}>
          {t('marketingLists.byProduct')}
        </Text>
        <Button
          onPress={() => {
            navigation.navigate('ChooseProduct', {getClientsByProduct})
          }}
          style={{marginVertical: 10}}
        > 
          {t('marketingLists.chooseProduct')}
        </Button>
      </View>

      { data && (
        <View>
          <Divider />
          {/* <Button onPress={async () => {
            try {
              await saveNewMarketingList(db, {
                date: new Date(),
                createdBy: context.user.uid,
                isDone: false,
                range: range, 
                data: data,
                detail: (productCategory?.name || product?.name),
                store
              })
            } catch(error) {
              Sentry.captureException(error)
            }
            
          }} style={{marginVertical: 10}}>
            {t('marketingLists.saveList')}
          </Button> */}
          <FlatList 
            data={data}
            renderItem={renderItem}
            ListEmptyComponent={() => (<Text appearance='hint' style={{
              marginTop: 100,
              textAlign: 'center'
            }}>
                {t('notFound')}
              </Text>
            )}
          />
        </View>
      )} 
      {isLoading &&  (
        <View style={styles.spinner}>
          <Spinner size="large" />
        </View>
      )}

      
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  label: {
    marginTop: 5,
    marginBottom: 5,
  },
  spinner: {
    justifyContent: 'center',
    alignItems: 'center'
  }
})